// src\context\ThemeContext.tsx
/* eslint-disable react-refresh/only-export-components */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import type { ThemeContextType } from '../types/index.ts';

// Create the context for theme management
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Custom hook to consume the ThemeContext.
 * Provides access to the current theme (darkMode) and the toggleTheme function.
 * Throws an error if used outside of a ThemeProvider.
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * ThemeProvider Component
 * Manages the theme state (dark mode) and provides it to its children
 * via the ThemeContext. It also handles persisting the theme preference
 * to localStorage and initializing from localStorage or system preference.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // State to hold the current theme mode (true for dark, false for light)
  const [darkMode, setDarkMode] = useState<boolean>(false);

  /**
   * Effect to initialize the theme when the component mounts.
   * It checks localStorage for a saved theme preference,
   * otherwise defaults to the user's system color scheme preference.
   */
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    const initialDark = savedTheme ? savedTheme === 'dark' : systemPrefersDark;
    setDarkMode(initialDark);
    document.documentElement.classList.toggle('dark', initialDark);
  }, []); // Empty dependency array ensures this runs only once on mount

  /**
   * Toggles the theme between dark and light modes.
   * Updates the darkMode state and saves the new preference to localStorage.
   */
  const toggleDarkMode = useCallback((): void => {
    setDarkMode(prevDarkMode => {
      const newDarkMode = !prevDarkMode;
      localStorage.setItem('theme', newDarkMode ? 'dark' : 'light');
      document.documentElement.classList.toggle('dark', newDarkMode);
      return newDarkMode;
    });
  }, []);

  const contextValue = useMemo<ThemeContextType>(() => ({
    darkMode,
    toggleDarkMode,
  }), [darkMode, toggleDarkMode]);

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};
